import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  href?: string
  external?: boolean
  children: ReactNode
}

const variantStyles = {
  primary:
    'bg-button-teal text-[#1a1f2e] shadow-button hover:-translate-y-0.5 hover:shadow-button-hover',
  secondary:
    'bg-transparent text-brand-light border border-brand-light/20 hover:bg-white/5 hover:border-brand-light/40',
  outline:
    'bg-transparent text-accent-teal border border-accent-teal hover:bg-accent-teal/10',
}

const sizeStyles = {
  sm: 'px-6 py-3 text-sm',
  md: 'px-9 py-[18px] text-base',
  lg: 'px-9 py-[18px] text-base',
}

export default function Button({
  variant = 'primary',
  size = 'md',
  href,
  external,
  children,
  className = '',
  ...props
}: ButtonProps) {
  const baseStyles = `
    inline-flex items-center justify-center gap-2
    font-semibold rounded-md
    transition-all duration-300
    cursor-pointer
    no-underline
    ${variantStyles[variant]}
    ${sizeStyles[size]}
    ${className}
  `

  if (href) {
    if (external) {
      return (
        <a href={href} className={baseStyles} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      )
    }
    return (
      <Link to={href} className={baseStyles}>
        {children}
      </Link>
    )
  }

  return (
    <button className={baseStyles} {...props}>
      {children}
    </button>
  )
}
